/**
 * Right panel of the Pipeline tab: read-only stage parameters and file notes.
 */

import { Badge, Button, EmptyState, Panel } from "@/components/ui/primitives";
import type { DwiStage, Modality, PwiStage, StageId } from "@/ipc/contract";
import { stageParams } from "@/pipeline/params";
import { ParamTable, paramsCaption } from "@/pipeline/ParamTable";
import { usePipelineSession } from "@/pipeline/session";
import { stageMeta } from "@/pipeline/stages";
import { useCurrentCase } from "@/store/appStore";

export function PipelineProperties() {
  const selected = usePipelineSession((s) => s.selectedStageKey);
  const draft = usePipelineSession((s) => s.draft);
  const scan = usePipelineSession((s) => s.scan);
  const currentCase = useCurrentCase();

  const parsed = parseStageKey(selected);
  const meta = parsed ? stageMeta(parsed.modality, parsed.id) : undefined;
  const groups = parsed ? stageParams(parsed.modality, parsed.id) : [];

  return (
    <Panel title={meta ? meta.label : "Properties"} className="h-full" bodyClassName="p-3 text-xs">
      {meta ? (
        <div className="space-y-3">
          <p className="text-neutral-600">{meta.description}</p>
          <div>
            <div className="mb-1 font-semibold text-neutral-700">Inputs</div>
            <ul className="list-disc space-y-0.5 pl-4 text-neutral-600">
              {meta.inputs.map((item) => (
                <li key={item}>{item.replaceAll("{id}", draft.subjectId || "{id}")}</li>
              ))}
            </ul>
          </div>
          <div>
            <div className="mb-1 font-semibold text-neutral-700">Outputs</div>
            <ul className="list-disc space-y-0.5 pl-4 text-neutral-600">
              {meta.outputs.map((item) => (
                <li key={item}>{item.replaceAll("{id}", draft.subjectId || "{id}")}</li>
              ))}
            </ul>
          </div>
          {groups.length === 0 ? (
            <p className="text-neutral-400">This step has no parameters worth naming: it runs one operation with no thresholds of its own.</p>
          ) : (
            <div>
              <div className="mb-1 font-semibold text-neutral-700">Parameters</div>
              <ParamTable groups={groups} subjectId={draft.subjectId} />
              <p className="mt-1.5 text-[10px] leading-snug text-neutral-400">{paramsCaption(groups)}</p>
            </div>
          )}
          <p className="text-[11px] text-neutral-400">
            Run this step launches only this stage; other checked steps stay selected for the next
            full protocol run.
          </p>
          {parsed ? (
            <Button
              size="sm"
              variant="primary"
              onClick={() =>
                void usePipelineSession.getState().run({
                  dwi: parsed.modality === "DWI" ? [parsed.id as DwiStage] : [],
                  pwi: parsed.modality === "PWI" ? [parsed.id as PwiStage] : [],
                })
              }
            >
              Run this step
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="space-y-3">
          {currentCase ? (
            <div className="space-y-1">
              <Row label="Case" value={currentCase.subjectId} />
              <Row label="Pipeline" value={currentCase.pipelineKind} />
              <Row label="Output" value={`${currentCase.outputRoot}/${currentCase.subjectId}`} />
            </div>
          ) : (
            <EmptyState title="Nothing selected" hint="Click a stage on the left to see its inputs and parameters." />
          )}
          {scan ? (
            <div>
              <div className="mb-1 font-semibold text-neutral-700">Scan</div>
              <div className="flex flex-wrap gap-1">
                <Badge tone="green">{scan.validCount} ok</Badge>
                <Badge tone="amber">{scan.warningCount} warn</Badge>
                <Badge tone="red">{scan.errorCount} error</Badge>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Panel>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-neutral-500">{label}</span>
      <span className="break-all text-right text-neutral-800">{value}</span>
    </div>
  );
}

function parseStageKey(key: string | null): { modality: Modality; id: StageId } | null {
  if (!key) return null;
  const idx = key.indexOf(":");
  if (idx < 0) return null;
  return { modality: key.slice(0, idx) as Modality, id: key.slice(idx + 1) as StageId };
}
